import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  AdvertiserFilterOption,
  AdvertiserFilterPageResult,
  fetchAllAdvertisersPage,
  fetchAssignedAdvertisersPage,
  resolveAdvertiserLabelsByIds,
} from '@features/campaigns/list/services/listAdvertisers';
import { QUERY_KEYS } from '@features/campaigns/shared/consts/queryKeys';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails';

function mergeSelectedAdvertiserOptions(
  options: AdvertiserFilterOption[],
  selectedValues: string[] | undefined,
  knownLabels?: Record<string, string>,
) {
  if (!selectedValues?.length) {
    return options;
  }

  const optionIds = new Set(options.map((option) => option.value));
  const missingSelectedOptions = selectedValues
    .filter((value) => !optionIds.has(value))
    .map((value) => ({ label: knownLabels?.[value] ?? value, value }));

  return [...missingSelectedOptions, ...options];
}

function getUnresolvedSelectedIds(
  selectedValues: string[] | undefined,
  loadedOptions: AdvertiserFilterOption[],
  knownLabels?: Record<string, string>,
) {
  const knownIds = new Set([...loadedOptions.map((option) => option.value), ...Object.keys(knownLabels ?? {})]);

  return selectedValues?.filter((value) => !knownIds.has(value)) ?? [];
}

function getNextAdvertiserFilterPageParam(lastPage: AdvertiserFilterPageResult) {
  if (!lastPage.meta) {
    return undefined;
  }

  const { currentPage, totalPages } = lastPage.meta;
  const nextPage = currentPage + 1;

  return nextPage <= totalPages ? { page: nextPage } : undefined;
}

export function useAdvertiserFilterOptions(
  search: string,
  selectedValues?: string[],
  advertiserLabels?: Record<string, string>,
) {
  const { id, isExternalAgency, isExternalDirectClient, isPortfolioSalesUser } = useUserDetails();

  const useAssigned = isExternalAgency || isExternalDirectClient || isPortfolioSalesUser;
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isFetching, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.ADVERTISERS_FILTER, id, useAssigned, debouncedSearch],
    queryFn: ({ pageParam }) =>
      useAssigned
        ? fetchAssignedAdvertisersPage(id, { search: debouncedSearch, page: pageParam.page })
        : fetchAllAdvertisersPage({ search: debouncedSearch, page: pageParam.page, includeInactive: true }),
    initialPageParam: { page: 1 },
    getNextPageParam: getNextAdvertiserFilterPageParam,
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
  });

  const loadedAdvertiserOptions = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data?.pages]);

  const unresolvedSelectedIds = useMemo(
    () => getUnresolvedSelectedIds(selectedValues, loadedAdvertiserOptions, advertiserLabels),
    [advertiserLabels, loadedAdvertiserOptions, selectedValues],
  );

  const { data: resolvedAdvertiserLabels } = useQuery({
    queryKey: [QUERY_KEYS.ADVERTISERS_FILTER, 'resolve-labels', id, useAssigned, unresolvedSelectedIds],
    queryFn: () => resolveAdvertiserLabelsByIds(unresolvedSelectedIds, id, useAssigned),
    enabled: Boolean(id) && unresolvedSelectedIds.length > 0,
    staleTime: Infinity,
  });

  const mergedAdvertiserLabels = useMemo(
    () => ({
      ...resolvedAdvertiserLabels,
      ...advertiserLabels,
    }),
    [advertiserLabels, resolvedAdvertiserLabels],
  );

  const mergedAdvertiserOptions = useMemo(
    () => mergeSelectedAdvertiserOptions(loadedAdvertiserOptions, selectedValues, mergedAdvertiserLabels),
    [loadedAdvertiserOptions, mergedAdvertiserLabels, selectedValues],
  );

  const totalItems = data?.pages.at(-1)?.meta?.totalItems ?? 0;

  return {
    advertiserOptions: mergedAdvertiserOptions,
    currentItemsCount: loadedAdvertiserOptions.length,
    totalItems,
    isLoading: isLoading || isFetching,
    isFetchingNextPage,
    fetchNextPage,
    resolvedAdvertiserLabels,
  };
}
